import { Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { cn } from "../../@shadcn/lib/utils";
import CouponsIcon from "../assets/CouponsIcon.svg";
import SpinnerIcon from "../assets/SpinnerIcon.svg";
import { GiftCardAppliedCardType } from "../types";

/**
 * Fallback masking, for a payload that names no `masked_number`. Matches the masking the older
 * gift-card rows use: the last 5 characters, padded out to 9.
 */
const MASK_CHARACTER = "*";
const MASK_TOTAL_LENGTH = 9;
const MASK_VISIBLE_CHARACTERS = 5;

const maskCode = (code: string) =>
  code.length <= MASK_VISIBLE_CHARACTERS
    ? code
    : code.slice(code.length - MASK_VISIBLE_CHARACTERS).padStart(MASK_TOTAL_LENGTH, MASK_CHARACTER);

export interface GiftCardAppliedRowProps {
  card: GiftCardAppliedCardType;
  iconUrl?: string;
  label?: string;
  formatAmount: (value: number) => string;
  onRemove: (card: GiftCardAppliedCardType) => void;
  canRemove?: boolean;
  isDeleting?: boolean;
  error?: Error | null;
  removeLabel?: string;
}

const GiftCardAppliedRow = ({
  card,
  iconUrl,
  label,
  formatAmount,
  onRemove,
  canRemove = true,
  isDeleting = false,
  error,
  removeLabel = "Remove gift card",
}: GiftCardAppliedRowProps) => {
  const [hasIconFailed, setHasIconFailed] = useState(false);
  const hasProviderIcon = Boolean(iconUrl) && !hasIconFailed;

  return (
    <div className="ui-flex ui-w-full ui-flex-col ui-gap-1 ui-rounded-xl  ui-p-3 hover:ui-bg-black/4 active:ui-bg-black/[0.08]">
      <div className="ui-flex ui-w-full ui-items-center ui-gap-4">
        {/* The ring frames a provider's own logo; the generic fallback is not framed. */}
        <div
          className={cn(
            "ui-size-10 ui-shrink-0 ui-overflow-hidden ui-rounded-full",
            hasProviderIcon && "ui-border ui-border-black/10",
          )}>
          {hasProviderIcon ? (
            <img
              src={iconUrl}
              alt={label ?? ""}
              className="ui-size-full ui-object-contain"
              onError={() => setHasIconFailed(true)}
            />
          ) : (
            <img src={CouponsIcon} alt="" className="ui-size-full ui-object-contain" />
          )}
        </div>


        <p className="ui-min-w-0 ui-flex-1 ui-truncate ui-text-text-md-medium ui-text-zinc-800">
          {card.masked_number ?? (card.transaction_id ? maskCode(card.transaction_id) : (label ?? ""))}
        </p>

        <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-2">
          <p className="ui-text-right ui-text-text-sm-semibold ui-text-zinc-700">
            {formatAmount(card.amount)}
          </p>
          {canRemove &&
            (isDeleting ? (
              <span className="ui-flex ui-items-center ui-w-8 ui-h-8 ui-justify-center ">
                <img src={SpinnerIcon} alt="" className="ui-size-[18px] ui-animate-spin" />
              </span>
            ) : (
              <button
                type="button"
                aria-label={removeLabel}
                onClick={() => onRemove(card)}
                className="ui-flex ui-items-center ui-rounded-lg ui-w-8 ui-h-8 ui-justify-center ui-text-zinc-500 hover:ui-bg-black/4">
                <Trash size={18} />
              </button>
            ))}
        </div>
      </div>

      {Boolean(error) && (
        <p className="ui-pl-14 ui-text-text-xs-medium ui-text-red-600">{error?.message}</p>
      )}
    </div>
  );
};

export default GiftCardAppliedRow;
